// requestsClient — AUTHENTICATED staff client for the unified outbound-requests
// review surface (GET /api/requests). Uses the shared `api` helper (Bearer token
// + 401 refresh-retry).
//
// Wire shapes mirror apps/api/src/routes/requestsReview.ts. Keep in sync.

import { api } from './api';
import type { AppfolioSyncDisplay } from './appfolioSync';

export type RequestKind =
  | 'rental_verification'
  | 'intake_designation'
  | 'intake_yard_care'
  | 'intake_animals'
  | 'lease'
  | 'adoption_addendum'
  | 'condition_report'
  /** WEL-48: the Move-Out Funds Direction Agreement (intake + signing rows). */
  | 'moveout_funds_direction'
  /** WEL-77: the standalone Security Deposit Payment Plan agreement (signing rows). */
  | 'payment_plan'
  /** WEL-48 manual-doc: a staff-uploaded PDF sent to a tenancy for signature. */
  | 'manual_document'
  /** WEL-92: the Exclusive Property Management Agreement (EPMA) — owner document. */
  | 'property_management_agreement'
  /** The Leaseholder Change, Release & Assumption Agreement — adds and releases
   *  residents on an existing tenancy (docs/leaseholder-change-agreement.md). */
  | 'leaseholder_change'
  /** The Leaseholder Removal & Release Agreement — removes an outgoing resident
   *  from an existing tenancy; the remaining residents stay on. */
  | 'leaseholder_removal'
  | 'animal_addendum'
  /** WEL-153: the PAID Animal Addendum — a current tenant adding a new animal, with
   *  the animal rent + deposit on it. Distinct from `animal_addendum`, which is the
   *  fee-free accommodation (RRA/ESA) addendum. Two phases share the occupancy as
   *  their group: the household form, then the signature rows. */
  | 'animal_addendum_paid'
  /** WEL-120: the Owner Contact Addendum (4th owner-onboarding doc, chains off an
   *  executed EPMA). Mirrors the server union so its Type cell + filter resolve. */
  | 'owner_contact_addendum'
  /** The City of Walla Walla utilities agreement (docs/city-utility-agreement-plan.md):
   *  the City's one-page form chained off an executed EPMA, one owner signer.
   *  Mirrors the server union so its Type cell + filter resolve. */
  | 'city_utility_agreement'
  /** WEL-141: the Owner W-9. Its row is REDACTED server-side — owner, property,
   *  recipient and full status/timeline, but no groupId, submission id, DocuSeal
   *  link or document, because the signed PDF carries an SSN/EIN and its sign
   *  page is publicly derivable from the groupId. View-only (canResend false). */
  | 'w9'
  /** Owner-sync spec §6: a VENDOR's W-9 (standalone request). Redacted exactly
   *  like the Owner W-9 — view-only, the signed PDF opens through the audited
   *  door. The record is still documentType 'w9' server-side. */
  | 'vendor_w9'
  /** WEL-166: the owner-intake phase of a property management agreement. */
  | 'owner_intake';

export type RequestStatus =
  | 'not_sent'
  | 'sent'
  | 'opened'
  | 'completed'
  | 'declined'
  | 'bounced'
  | 'expired'
  | 'no_contact'
  | 'internal_review'
  | 'skipped'
  /** WEL-46: a request a staff member explicitly canceled/withdrew. */
  | 'cancelled';

export type RequestTone =
  | 'neutral'
  | 'info'
  | 'accent'
  | 'success'
  | 'warning'
  | 'danger'
  | 'muted';

/** One staff-initiated manual resend/nudge (WEL-20 Phase 2). */
export interface ManualSendEntry {
  at: string;
  by: string;
  bySub?: string;
}

// --- WEL-57: per-recipient activity history (carried on row.data.activity) ----

export type RequestEventKind =
  | 'sent'
  | 'reminded'
  | 'email_opened'
  | 'page_opened'
  | 'completed'
  | 'signed'
  | 'responded'
  | 'declined'
  | 'bounced'
  | 'cancelled'
  // WEL-171: WHP emailed this signer the lease's "Next Steps" letter.
  | 'next_steps_sent';

/** One labeled, timestamped action in a recipient's transaction history. */
export interface ActivityEvent {
  kind: RequestEventKind;
  at: string;
  /** True when synthesized from a first-instant timestamp (backfill), not logged. */
  synthesized: boolean;
  meta?: Record<string, unknown>;
}

/** One requested party's activity stream + open counts + pending action. */
export interface RecipientActivity {
  recipient: string;
  name?: string;
  email?: string;
  role?: string;
  events: ActivityEvent[];
  emailOpenCount: number;
  pageOpenCount: number;
  firstEmailOpenAt?: string;
  lastEmailOpenAt?: string;
  firstPageOpenAt?: string;
  lastPageOpenAt?: string;
  completedAt?: string;
  /** What this party still needs to do (absent once done/declined/cancelled). */
  pending?: string;
}

/** The full activity history for a request — every party + the "still to happen" list. */
export interface RequestActivity {
  recipients: RecipientActivity[];
  pending: string[];
}

/** Read the WEL-57 activity history off a row's data payload (best-effort). */
export function activityOf(row: OutboundRequestRow): RequestActivity | undefined {
  const d = row.data;
  if (d && typeof d === 'object' && 'activity' in d) {
    const a = (d as { activity?: unknown }).activity;
    if (a && typeof a === 'object' && Array.isArray((a as RequestActivity).recipients)) {
      return a as RequestActivity;
    }
  }
  return undefined;
}

export interface OutboundRequestRow {
  id: string;
  kind: RequestKind;
  kindLabel: string;
  /** WEL-99: true when a lease row is actually a RENEWAL (group `renewal-<ulid>`).
   *  The kind stays 'lease' (so the "Lease" filter surfaces both); the Type cell
   *  renders "Renewal" vs "Lease" off this flag. Undefined on non-lease rows. */
  isRenewal?: boolean;
  groupId: string;
  groupLabel?: string;
  /** WEL-64: comma-joined applicant names in this request's application group
   *  (drives the Applicants column, the grouped "Unit – Applicants" header, and
   *  is searchable). Undefined for kinds with no group/applicants. */
  applicants?: string;
  /** WEL-64: WHP unit label/number for this group (drives the Unit column +
   *  grouped header). Undefined when unresolved ("—"). */
  unit?: string;
  /** WEL-64: property name/address for this group (drives the Property column).
   *  Undefined when unresolved ("—"). */
  property?: string;
  /** WEL-101: rental-verification rows only — the PRIOR residence being verified
   *  (the applicant-claimed address), deliberately a DIFFERENT address than
   *  `property`/`unit` (the WHP property applied for). Rendered as a labeled
   *  "Verifying …" line under the Property cell; searchable. */
  priorAddress?: string;
  recipientName?: string;
  recipientEmail?: string;
  recipientRole?: string;
  /** The applicant/tenant/household this request is about (when ≠ the recipient). */
  subjectLabel?: string;
  status: RequestStatus;
  statusLabel: string;
  tone: RequestTone;
  sentAt?: string;
  openedAt?: string;
  completedAt?: string;
  lastReminderAt?: string;
  reminderCount?: number;
  /** Most recent contact instant (max of sentAt/lastReminderAt/manual sends). Drives the 12h confirm. */
  lastSentAt?: string;
  /** Staff-initiated manual resends/nudges to this recipient (history popover). */
  manualSends?: ManualSendEntry[];
  /** Whether a manual resend/nudge is offered for this row. */
  canResend: boolean;
  /**
   * Whether Cancel is offered: the request is still outstanding, whether or not
   * its recipient has an email (WEL-181/182). Optional so a page talking to an
   * API that predates the field falls back to `canResend` — see `offersGenericCancel`.
   */
  canCancel?: boolean;
  needsAttention: boolean;
  data?: unknown;
  deepLinkPath?: string;
  /**
   * WEL-50: AppFolio rental-application deep link — rental-verification rows only.
   * `${APPFOLIO_WEB_BASE}/rental_applications/:id` for the applicant's application.
   */
  appfolioApplicationUrl?: string;
  /** Numeric DocuSeal submission id — signature-backed rows only. */
  docusealSubmissionId?: number;
  /** Staff/admin DocuSeal submission link for follow-up — signature-backed rows only. */
  docusealUrl?: string;
  /** AppFolio occupancy-document sync status — lease/adoption_addendum rows only (one chip per group). */
  appfolioSync?: AppfolioSyncDisplay;
  /**
   * WEL-135: the staff member who clicked Send, resolved from the durable
   * create-claim. Only the staff-initiated documents carry one — anything the
   * system sends on its own leaves this undefined. Rendered as initials in the
   * "Init" column, with the full name on hover.
   */
  sentBy?: string;
  /**
   * WEL-22 (req 5): true on a SUPERSEDED designation row whose holder submitted a
   * designee — staff can REVIVE it (promote it to the merged group's authoritative
   * household designation). Drives the "Use this designation" action.
   */
  revivable?: boolean;
  /** The merged (authoritative) group a revivable superseded designation promotes into. */
  revivableInto?: string;
}

export interface RequestsSummary {
  count: number;
  requests: OutboundRequestRow[];
}

export async function listRequests(): Promise<RequestsSummary> {
  return api.get<RequestsSummary>('/api/requests');
}

// --- WEL-20: per-application-group rollup (Applications badge + grouped view) ---

/**
 * One pre-lease intake form's status (animals / resident designation / yard
 * care) for the per-application intake panel — WEL-20. Human-readable; no token.
 */
export interface IntakeFormStatus {
  kind: 'intake_designation' | 'intake_yard_care' | 'intake_animals';
  label: string;
  status: RequestStatus;
  statusLabel: string;
  tone: RequestTone;
  recipientName?: string;
  sentAt?: string;
  openedAt?: string;
  completedAt?: string;
  needsAttention: boolean;
}

export interface GroupFormSummary {
  groupId: string;
  groupLabel?: string;
  total: number;
  completed: number;
  opened: number;
  sent: number;
  notSent: number;
  problem: number;
  needsAttention: number;
  badgeTone: RequestTone;
  stale: boolean;
  /** Per-form status of the pre-lease intake asks specifically (WEL-20). */
  intakeForms: IntakeFormStatus[];
}

export interface GroupSummaryResponse {
  count: number;
  groups: GroupFormSummary[];
}

/** Per-application-group form-status rollup, keyed for the Applications-list badge. */
export async function requestsGroupSummary(): Promise<GroupSummaryResponse> {
  return api.get<GroupSummaryResponse>('/api/requests/group-summary');
}

// --- WEL-20 Phase 2: manual resend / nudge ---

export interface ResendResult {
  id: string;
  ok: boolean;
  to?: string;
  at?: string;
  reason?: string;
}

export interface ResendResponse {
  sent: number;
  failed: number;
  results: ResendResult[];
}

/** Manually resend/nudge one or more outstanding requests (resets their reminder clock). */
export async function resendRequests(ids: string[]): Promise<ResendResponse> {
  return api.post<ResendResponse>('/api/requests/resend', { ids });
}

// --- WEL-46: bulk cancel / withdraw ----------------------------------------

export interface CancelResult {
  id: string;
  ok: boolean;
  reason?: string;
}

export interface CancelResponse {
  cancelled: number;
  failed: number;
  results: CancelResult[];
}

/**
 * Cancel/withdraw one or more outstanding requests (WEL-46). For signature-backed
 * requests this also withdraws the DocuSeal signing request. `notify` (default
 * false) emails the recipients that the request was canceled; `message` is an
 * optional free-text note included in that email when `notify` is on.
 */
export async function cancelRequests(
  ids: string[],
  opts: { notify: boolean; message?: string },
): Promise<CancelResponse> {
  return api.post<CancelResponse>('/api/requests/cancel', {
    ids,
    notify: opts.notify,
    ...(opts.message ? { message: opts.message } : {}),
  });
}

// --- WEL-22 (req 5): revive a specific superseded designation ----------------

/** What the revive endpoint returns (the updated authoritative designation block). */
export interface ReviveDesignationResponse {
  ok: boolean;
  designation?: {
    status: string;
    data?: { designeeName?: string; relationship?: string; phone?: string; email?: string; filledBy?: string };
    revivedAt?: string;
    revivedByDisplay?: string;
    revivedFromApplicantId?: string;
  };
}

/**
 * Promote a SPECIFIC superseded applicant's already-submitted designation to the
 * merged group's AUTHORITATIVE household designation (WEL-22 req 5). `intoGroupId`
 * is the merged group (row.revivableInto); `fromGroupId` is the superseded
 * source's record key (row.groupId). Reversible + audited on the server.
 */
export async function reviveDesignation(
  intoGroupId: string,
  fromGroupId: string,
): Promise<ReviveDesignationResponse> {
  return api.post<ReviveDesignationResponse>(
    `/api/intake/${encodeURIComponent(intoGroupId)}/designation/revive`,
    { fromGroupId },
  );
}

// WEL-54: intake_animals empty-list copy lives in a dependency-free module so
// it stays unit-testable in isolation. Re-exported here for app call sites.
export { animalsEmptyLabel } from './intakeAnimalsLabel';

/**
 * WEL-141: a short-lived link to a COMPLETED Owner W-9.
 *
 * Keyed on the row's versionId — the dashboard never carries a W-9's groupId,
 * because that value derives the document's PUBLIC (no-login) signing page. The
 * server resolves the record, logs who opened it, and returns a URL that expires
 * in five minutes.
 */
export function getW9DocumentUrl(versionId: string): Promise<{ url: string }> {
  return api.get(`/api/requests/w9/${encodeURIComponent(versionId)}/document`);
}
